import React from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatDateEU } from "@/components/shared/helpers";

// Calendar View Component for stays
export const StaysCalendar = ({ stays, month, onMonthChange, onStayClick }) => {
  const DAYS_FR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  const MONTHS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
  
  // Get first day of month and number of days
  const year = month.getFullYear();
  const monthIdx = month.getMonth();
  const firstDay = new Date(year, monthIdx, 1);
  const lastDay = new Date(year, monthIdx + 1, 0);
  const daysInMonth = lastDay.getDate();
  
  // Get day of week for first day (Monday = 0)
  let startDayOfWeek = firstDay.getDay() - 1;
  if (startDayOfWeek < 0) startDayOfWeek = 6;
  
  // Build calendar grid
  const days = [];
  for (let i = 0; i < startDayOfWeek; i++) {
    days.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(d);
  }
  
  // Get stays for this month
  const getStaysForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return stays.filter(s => {
      return s.start_date <= dateStr && s.end_date >= dateStr;
    });
  };
  
  const isStartDay = (stay, day) => {
    const dateStr = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return stay.start_date === dateStr;
  };
  
  const isEndDay = (stay, day) => {
    const dateStr = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return stay.end_date === dateStr;
  };
  
  const prevMonth = () => {
    onMonthChange(new Date(year, monthIdx - 1, 1));
  };
  
  const nextMonth = () => {
    onMonthChange(new Date(year, monthIdx + 1, 1));
  };
  
  const today = new Date();
  const isToday = (day) => {
    return day === today.getDate() && monthIdx === today.getMonth() && year === today.getFullYear();
  };
  
  return (
    <div data-testid="stays-calendar" className="rounded-xl border border-white/10 bg-white/[0.08] p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button
          data-testid="calendar-prev-month"
          variant="outline"
          size="sm"
          className="border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10"
          onClick={prevMonth}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div data-testid="calendar-month-label" className="text-lg font-semibold text-slate-100">
          {MONTHS_FR[monthIdx]} {year}
        </div>
        <Button
          data-testid="calendar-next-month"
          variant="outline"
          size="sm"
          className="border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10"
          onClick={nextMonth}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Days header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {DAYS_FR.map(d => (
          <div key={d} className="text-center text-xs font-medium text-slate-400 py-1">
            {d}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, idx) => {
          const dayStays = getStaysForDay(day);
          return (
            <div
              key={idx}
              data-testid={day ? `calendar-day-${day}` : `calendar-empty-${idx}`}
              className={`min-h-[70px] rounded-lg p-1 ${
                day 
                  ? isToday(day)
                    ? "bg-violet-500/20 border border-violet-500/50"
                    : "bg-white/[0.04] hover:bg-white/[0.08]"
                  : ""
              }`}
            >
              {day && (
                <>
                  <div className={`text-xs font-medium mb-1 ${isToday(day) ? "text-violet-400" : "text-slate-400"}`}>
                    {day}
                  </div>
                  <div className="space-y-0.5">
                    {dayStays.slice(0, 2).map((stay, sIdx) => {
                      const isStart = isStartDay(stay, day);
                      const isEnd = isEndDay(stay, day);
                      const bgColor = stay.stay_type === "tenant" ? "bg-violet-500" : "bg-emerald-500";
                      return (
                        <div
                          key={stay.id || sIdx}
                          data-testid={`calendar-stay-${stay.id}`}
                          className={`text-[10px] px-1 py-0.5 text-white truncate cursor-pointer hover:opacity-80 ${bgColor} ${
                            isStart && isEnd ? "rounded" :
                            isStart ? "rounded-l" :
                            isEnd ? "rounded-r" : ""
                          }`}
                          onClick={() => onStayClick?.(stay)}
                          title={`${stay.stay_type === "tenant" ? "Loc" : "Prop"}: ${formatDateEU(stay.start_date)} → ${formatDateEU(stay.end_date)}${stay.tenant?.name ? ` - ${stay.tenant.name}` : ""}`}
                        >
                          {isStart ? (stay.tenant?.name?.split(" ")[0] || (stay.stay_type === "tenant" ? "Loc" : "Prop")) : ""}
                        </div>
                      );
                    })}
                    {dayStays.length > 2 && (
                      <div className="text-[9px] text-slate-400">+{dayStays.length - 2}</div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-white/10">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-violet-500"></div>
          <span className="text-xs text-slate-300">Locataire</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-emerald-500"></div>
          <span className="text-xs text-slate-300">Propriétaire</span>
        </div>
      </div>
    </div>
  );
};
